import { createApi } from "@reduxjs/toolkit/query/react";
import { createCryptoBaseQuery } from "./cryptoBaseQuery";

const getCurrentAccessToken = () => {
  return localStorage.getItem("accessToken");
};

export const setCurrentAccessToken = (token) => {
  localStorage.setItem("accessToken", token);
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: createCryptoBaseQuery({
    getCurrentAccessToken,
  }),
  tagTypes: ["Project", "Task", "Sprint", "ProjectMember"],
  endpoints: (builder) => ({
    getUserProfile: builder.query({
      query: (id) => ({
        url: "/auth/getProfile/" + id,
        method: "GET",
      }),
    }),

    loginUser: builder.mutation({
      query: (body) => ({
        url: "/auth/login",
        method: "POST",
        body,
      }),
    }),

    registerUser: builder.mutation({
      query: (body) => ({
        url: "/auth/register",
        method: "POST",
        body,
      }),
    }),
    createTask: builder.mutation({
      query: (body) => ({
        url: "/issue/insert",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Task"],
    }),
    updateTask: builder.mutation({
      query: (payload) => ({
        url: "/issue/updateRecord",
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["Task"],
    }),
    deleteTask: builder.mutation({
      query: (id) => ({
        url: "/issue/deleteRecord",
        method: "POST",
        body: { issueId: id },
      }),
      invalidatesTags: ["Task"],
    }),
    projectMember: builder.mutation({
      query: (body) => ({
        url: "/projectMember/insertMany",
        method: "POST",
        body,
      }),
    }),
    insertProjectMember: builder.mutation({
      query: (body) => ({
        url: "/projectMember/insert",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ProjectMember"],
    }),
    getProjectMembers: builder.query({
      query: (projectId) => ({
        url: projectId
          ? `/projectMember/getAllRecord?projectId=${projectId}`
          : "/projectMember/getAllRecord",
        method: "GET",
      }),
      transformResponse: (response) => {
        const findFirstArray = (obj) => {
          if (Array.isArray(obj)) return obj;
          if (obj && typeof obj === "object") {
            const nested = obj.data || obj.schema || obj.list || obj.records;
            if (Array.isArray(nested)) return nested;
            if (nested && typeof nested === "object") return findFirstArray(nested);
            for (const key in obj) {
              if (Array.isArray(obj[key])) return obj[key];
              if (obj[key] && typeof obj[key] === "object") {
                const found = findFirstArray(obj[key]);
                if (found) return found;
              }
            }
          }
          return null;
        };
        const raw = findFirstArray(response) || [];
        return raw.map((item) => item.schema || item);
      },
      providesTags: ["ProjectMember"],
    }),
    getOrganizationMembers: builder.query({
      query: (organizationId) => ({
        url: `/organizationMember/getAllRecord?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    getProjects: builder.query({
      query: () => ({
        url: "/project/getAllRecord",
        method: "GET",
      }),
      transformResponse: (response) => {
        console.log("DEBUG: Raw response from /project/getAllRecord:", response);

        const findFirstArray = (obj) => {
          if (Array.isArray(obj)) return obj;
          if (obj && typeof obj === 'object') {
            const nested = obj.data || obj.schema || obj.list || obj.records;
            if (Array.isArray(nested)) return nested;
            if (nested && typeof nested === 'object') return findFirstArray(nested);

            for (const key in obj) {
              if (Array.isArray(obj[key])) return obj[key];
              if (obj[key] && typeof obj[key] === 'object') {
                const found = findFirstArray(obj[key]);
                if (found) return found;
              }
            }
          }
          return null;
        };

        const rawArray = findFirstArray(response) || [];
        console.log("DEBUG: Extracted projects array:", rawArray);

        let mapped = rawArray.map(item => item.schema || item);
        try {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const userObj = JSON.parse(userStr);
            const currentUserId = userObj.id || userObj._id || userObj.userId;

            // We'll keep the projects that are returned by the API, but log their ownership
            console.log("DEBUG: Current User ID:", currentUserId);
            mapped.forEach(p => {
              const ownerId = p.userId || p.ownerId;
              console.log(`DEBUG: Project "${p.name || p.projectName}" owner: ${ownerId} isMine: ${ownerId === currentUserId}`);
            });
            // If the API is already supposed to return only allowed projects, 
            // the strict filter here might be over-restricting for contribution projects.
            // We will let them through now so TaskTools can categorize them.
          }
        } catch (e) {
          console.error("Filter error:", e);
        }
        return mapped;
      },
      providesTags: ["Project"],
    }),
    getTasks: builder.query({
      query: (projectId) => ({
        url: projectId ? `/issue/getAllRecord?projectId=${projectId}` : "/issue/getAllRecord",
        method: "GET",
      }),
      transformResponse: (response) => {
        console.log("DEBUG: Raw response from /issue/getAllRecord:", response);

        const findFirstArray = (obj) => {
          if (Array.isArray(obj)) return obj;
          if (obj && typeof obj === 'object') {
            // Check common wrappers first
            const nested = obj.data || obj.schema || obj.list || obj.records;
            if (Array.isArray(nested)) return nested;
            if (nested && typeof nested === 'object') return findFirstArray(nested);

            // Search all keys if not found in common wrappers
            for (const key in obj) {
              if (Array.isArray(obj[key])) return obj[key];
              if (obj[key] && typeof obj[key] === 'object') {
                const found = findFirstArray(obj[key]);
                if (found) return found;
              }
            }
          }
          return null;
        };

        const rawArray = findFirstArray(response) || [];
        console.log("DEBUG: Extracted raw array:", rawArray);

        let mapped = rawArray.map(item => item.schema || item);
        try {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const userObj = JSON.parse(userStr);
            const currentUserId = userObj.id || userObj._id || userObj.userId;
            if (currentUserId) {
              const uid = String(currentUserId);
              mapped = mapped.filter(task =>
                String(task.userId) === uid ||
                String(task.assigneeId) === uid ||
                String(task.reporterId) === uid
              );
            }
          }
        } catch (e) {
          console.error("Filter error:", e);
        }
        return mapped;
      },
      providesTags: ["Task"],
    }),
    createProject: builder.mutation({
      query: (body) => ({
        url: "/project/insert",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Project"],
    }),
    deleteProject: builder.mutation({
      query: (projectId) => ({
        url: `/project/deleteRecord?projectId=${projectId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Project", "Task"],
    }),
    searchUser: builder.query({
      query: (email) => ({
        url: `/user/searchRecord?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    searchOneRecord: builder.query({
      query: (email) => ({
        url: `/user/searchOneRecord?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    searchOneOrganizationMember: builder.query({
      query: (userId) => ({
        url: `/organizationMember/searchOneRecord?userId=${userId}`,
        method: "GET",
      }),
    }),
    getAllSprints: builder.query({
      query: () => ({
        url: "/sprint/getAllRecord",
        method: "GET",
      }),
      providesTags: ["Sprint"],
    }),
    createSprint: builder.mutation({
      query: (body) => ({
        url: "/sprint/insert",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Sprint"],
    }),
  }),
});

export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useGetUserProfileQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useProjectMemberMutation,
  useInsertProjectMemberMutation,
  useLazySearchUserQuery,
  useLazySearchOneRecordQuery,
  useLazySearchOneOrganizationMemberQuery,
  useGetAllSprintsQuery,
  useCreateSprintMutation,
  useGetProjectsQuery,
  useGetTasksQuery,
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useGetOrganizationMembersQuery,
  useGetProjectMembersQuery,
} = api;
